import { MENU_CONFIG } from '../config/menuConfig';
import type { MenuItem } from '../types';

type HasResourceAccess = (resource: string) => boolean;
type HasPermission = (resource: string, action: string) => boolean;

/**
 * Mappa un path di rotta alla resource che ne governa l'accesso.
 * Mirror della logica in Layout.tsx > canAccessMenuItem (priority 1, path-based).
 * Vedi src/components/Layout.tsx:1107-1177.
 */
export function getResourceForPath(path: string): string | null {
  if (!path) return null;

  if (path.startsWith('/dashboard')) return 'dashboard';
  if (path.startsWith('/my-tasks')) return 'projects';
  if (path.startsWith('/clients')) return 'clients';
  if (path.startsWith('/leads')) return 'leads';
  if (path.startsWith('/contacts')) return 'contacts';
  if (path.startsWith('/projects')) return 'projects';
  if (path.startsWith('/deals')) return 'deals';
  if (path.startsWith('/fattureincloud')) return 'fattureincloud';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/tickets')) return 'tickets';
  if (path.startsWith('/analytics')) return 'reports';
  if (path.startsWith('/reports')) return 'reports';
  if (path.startsWith('/documents')) return 'documents';
  if (path.startsWith('/calendar')) return 'calendar';
  if (path.startsWith('/inventory') || path.startsWith('/magazzino')) return 'inventory';
  if (path.startsWith('/whatsapp')) return 'whatsapp';
  if (path.startsWith('/commissions')) return 'commissions';
  if (path === '/services/costs') return 'client_expenses';
  if (path.startsWith('/services')) return 'services';
  if (path.startsWith('/payment-methods')) return 'payment_methods';
  if (path.startsWith('/referrers')) return 'referrers';
  if (path.startsWith('/partner-tasks')) return 'partner_tasks';
  if (path.startsWith('/partners')) return 'external_partners';
  if (path.startsWith('/meetings')) return 'ai_meeting_bot';
  if (path.startsWith('/newsletter')) return 'newsletter';
  if (path.startsWith('/lead-forms')) return 'lead_forms';
  if (path.startsWith('/qr-codes')) return 'qr_codes';
  if (path.startsWith('/bookings')) return 'booking';
  if (path.startsWith('/expenses')) return 'client_expenses';
  if (path.startsWith('/wiki')) return 'wiki';
  if (path.startsWith('/gmail')) return 'gmail';
  if (path.startsWith('/surveys')) return 'surveys';
  if (path.startsWith('/marketing')) return 'marketing';

  return null;
}

interface FindOptions {
  excludeDashboard?: boolean;
}

function isItemAccessible(
  item: MenuItem,
  hasResourceAccess: HasResourceAccess,
  hasPermission: HasPermission,
): boolean {
  if (item.resource && item.action) {
    return hasPermission(item.resource, item.action);
  }
  if (item.resource) {
    return hasResourceAccess(item.resource);
  }
  if (item.to) {
    const resource = getResourceForPath(item.to);
    if (resource) return hasResourceAccess(resource);
  }
  return false;
}

function flattenLeaves(items: MenuItem[]): MenuItem[] {
  const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out: MenuItem[] = [];
  for (const item of sorted) {
    if (item.hidden) continue;
    if (item.children && item.children.length > 0) {
      out.push(...flattenLeaves(item.children));
    } else if (item.to) {
      out.push(item);
    }
  }
  return out;
}

/**
 * Ritorna la prima rotta a cui l'utente ha accesso, in base ai suoi permessi.
 * Priorita':
 *   1. /my-tasks (se hasResourceAccess('projects'))
 *   2. iterazione MENU_CONFIG in ordine, scendendo nei children
 *   3. /settings come ultimo fallback (se accessibile), altrimenti /login
 */
export function findFirstAccessibleRoute(
  hasResourceAccess: HasResourceAccess,
  hasPermission: HasPermission,
  options: FindOptions = {},
): string {
  const { excludeDashboard = false } = options;

  if (hasResourceAccess('projects')) {
    return '/my-tasks';
  }

  const leaves = flattenLeaves(MENU_CONFIG);
  for (const item of leaves) {
    if (!item.to) continue;
    if (excludeDashboard && item.to.startsWith('/dashboard')) continue;
    if (item.to.startsWith('/my-tasks')) continue;
    if (isItemAccessible(item, hasResourceAccess, hasPermission)) {
      return item.to;
    }
  }

  if (hasResourceAccess('settings')) return '/settings';
  return '/login';
}
